import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './Select.module.scss';
import ArrowDown from '@/assets/Icons/Arrow-Down.svg?react';
import CheckmarkIcon from '@/assets/Icons/Done-Checkmark.svg?react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import SearchIcon from '@/assets/Icons/Search.svg?react';
import { isEmpty, toLower } from 'lodash';
import CloseIcon from '@/assets/Icons/Close.svg?react';
import { ListChildComponentProps, VariableSizeList as List } from 'react-window';
import { calculateTextHeight } from '@/utils/domUtils.ts';
import Skeleton from 'react-loading-skeleton';
import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import OutsideClickHandler from 'react-outside-click-handler';

export type Value = string | number | undefined;

export interface Option {
	value: Value;
	label: string;
	description?: string;
	selectedDescription?: string;
	isGroup?: boolean;
}

export interface SelectProps {
	className?: string;
	label?: string;
	ariaLabel?: string;
	placeholder?: string;
	value: Value;
	onChange: (newValue: Value) => void;
	options: Option[] | Record<string, Option[]>;
	errorMessage?: string;
	touched?: boolean;
	disabled?: boolean;
	name?: string;
	tableSelect?: boolean;
	searchable?: boolean;
	required?: boolean;
	wildcardVisible?: boolean;
	alwaysShowPlaceholder?: boolean;
	compactOptions?: boolean;
}

interface SelectOptionListData {
	options: Option[];
	handleOptionClick: (newValue: Value) => void;
	selectedValue: Value;
}

const Row: React.FC<ListChildComponentProps<SelectOptionListData>> = React.memo(({ index, style, data }) => {
	const { options, handleOptionClick, selectedValue } = data;
	const option = options[index];

	if (!option) return null;

	if (option.isGroup) {
		return (
			<div style={style} className={styles.groupLabel}>
				{option.label}
			</div>
		);
	}

	return (
		<div
			style={style}
			className={classNames(styles.rowWrapper, {
				[styles.selected]: option.value === selectedValue,
			})}
		>
			<FlexBlock
				justifyContent="space-between"
				alignItems="center"
				className={styles.option}
				aria-selected={option.value === selectedValue}
				onMouseDown={() => handleOptionClick(option.value)}
			>
				<span className={styles.optionText}>
					<span className={styles.optionLabel}>{option.label}</span>
					{option.description && <span className={styles.optionDescription}>{option.description}</span>}
				</span>
				{option.value === selectedValue && <CheckmarkIcon />}
			</FlexBlock>
		</div>
	);
});

const Select: React.FC<SelectProps> = ({
	label,
	ariaLabel,
	onChange,
	placeholder,
	className,
	options,
	value,
	errorMessage,
	touched,
	disabled,
	name,
	tableSelect,
	searchable = false,
	required = false,
	wildcardVisible = false,
	alwaysShowPlaceholder = false,
	compactOptions = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0);

	// Floating UI setup
	const { refs, floatingStyles } = useFloating({
		placement: 'bottom',
		middleware: [offset(12), flip({ fallbackPlacements: ['bottom', 'top'] }), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
		strategy: 'fixed',
	});

	const handleOpen = () => {
		if (!disabled) setIsOpen(!isOpen);
	};

	const handleClose = () => {
		setIsOpen(false);
		setSearchQuery('');
	};

	const handleOptionClick = (newValue: Value) => {
		onChange(newValue);
		handleClose();
	};

	const handleClearFieldValue = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onChange(undefined);
		handleClose();
	};

	useEffect(() => {
		const updateWidth = () => {
			if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
		};
		updateWidth();
		window.addEventListener('resize', updateWidth);
		return () => window.removeEventListener('resize', updateWidth);
	}, []);

	const getDisplayedOptions = (): Option[] => {
		if (Array.isArray(options)) {
			return searchQuery
				? options.filter((option, index, array) => {
						const matchesSearch = toLower(option.label).includes(toLower(searchQuery.trim()));
						const isCurrentGroup = option.isGroup;

						// Check if the current option is a group
						if (isCurrentGroup) {
							// Determine if there are any matching options in this group
							const nextGroupIndex = array.findIndex((opt, i) => i > index && opt.isGroup);
							const groupOptions = array.slice(
								index + 1,
								nextGroupIndex === -1 ? undefined : nextGroupIndex,
							);
							const hasMatchesInGroup = groupOptions.some(opt =>
								toLower(opt.label).includes(toLower(searchQuery.trim())),
							);
							return hasMatchesInGroup;
						}

						return matchesSearch;
					})
				: options;
		} else {
			const flattenedOptions: Option[] = [];
			Object.entries(options).forEach(([groupLabel, groupOptions]) => {
				const filtered = searchQuery
					? groupOptions.filter(option => toLower(option.label).includes(toLower(searchQuery.trim())))
					: groupOptions;
				if (filtered.length > 0) {
					flattenedOptions.push({ value: groupLabel, label: groupLabel, isGroup: true });
					flattenedOptions.push(...filtered);
				}
			});
			return flattenedOptions;
		}
	};

	const listRef = useRef<List>(null);

	const displayedOptions = useMemo(getDisplayedOptions, [options, searchQuery]);
	const selectedOption = useMemo(() => {
		if (value === undefined || value === null) {
			return undefined;
		}

		return Array.isArray(options)
			? options.find(option => option.value === value)
			: Object.values(options)
					.flat()
					.find(option => option.value === value);
	}, [options, value]);
	const selectedOptionDescription = selectedOption?.selectedDescription || selectedOption?.description;

	// IMPORTANT: reset react-window cache when options/width change
	useEffect(() => {
		listRef.current?.resetAfterIndex(0, true);
	}, [displayedOptions.length, searchQuery, containerWidth]);

	// stable itemKey so recycling doesn’t mix rows
	const itemKey = useCallback((index: number, data: SelectOptionListData) => {
		const opt = data.options[index];
		return opt?.isGroup ? `__group__${opt.label}` : String(opt?.value ?? `idx-${index}`);
	}, []);

	const renderOptions = () => {
		const optionTextHeightOptions = compactOptions ? { fontSize: 14, paddingY: 14 } : undefined;
		const getOptionHeight = (option: Option) =>
			calculateTextHeight(option.label, containerWidth, optionTextHeightOptions) + (option.description ? 18 : 0);

		const calculateListHeight = () => {
			if (displayedOptions.length <= 5) {
				let elementsHeight = 0;
				displayedOptions.forEach(option => {
					elementsHeight += getOptionHeight(option);
				});
				return elementsHeight > 220 ? 220 : elementsHeight;
			}
			return searchable ? 220 : 272;
		};

		return (
			<List
				height={calculateListHeight()}
				itemCount={displayedOptions.length}
				itemSize={index => getOptionHeight(displayedOptions[index])}
				width="100%"
				itemKey={itemKey}
				itemData={{
					options: displayedOptions,
					handleOptionClick,
					selectedValue: value,
				}}
			>
				{Row}
			</List>
		);
	};

	return (
		<FlexBlock flexDirection="column" className={classNames(styles.selectWrapper, className)}>
			{!!label && (
				<span className={styles.label}>
					{label}
					{wildcardVisible && '*'}
				</span>
			)}
			<div className={classNames(styles.selectButton)} ref={containerRef}>
				<div
					ref={refs.setReference}
					className={classNames(styles.select, {
						[styles.disabled]: disabled,
						[styles.tableSelect]: tableSelect,
					})}
					onClick={handleOpen}
					role="combobox"
					tabIndex={0}
					aria-label={ariaLabel ?? label}
					aria-disabled={disabled}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-required={required}
					aria-controls={isOpen ? 'select-options' : undefined}
					data-name={name}
				>
					<span className={styles.selectedOptionLabel}>
						{value !== undefined && value !== null && !alwaysShowPlaceholder ? (
							selectedOption ? (
								selectedOptionDescription ? (
									<span className={styles.selectedOptionContent}>
										<span className={styles.selectedOptionMain}>{selectedOption.label}</span>
										<span className={styles.selectedOptionDescription}>
											{selectedOptionDescription}
										</span>
									</span>
								) : (
									selectedOption.label
								)
							) : (
								<Skeleton height={17} width="100%" />
							)
						) : (
							placeholder || 'Select...'
						)}
					</span>
					<FlexBlock>
						{!required && value !== undefined && value !== null && !alwaysShowPlaceholder && !disabled && (
							<button
								className={styles.clearIcon}
								onClick={handleClearFieldValue}
								aria-label="Clear selection"
							>
								<CloseIcon />
							</button>
						)}
						<div className={classNames(styles.icon, { [styles.open]: isOpen })}>
							<ArrowDown />
						</div>
					</FlexBlock>
				</div>
			</div>
			{isOpen && (
				<FloatingPortal>
					<OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
						<div
							ref={refs.setFloating}
							style={{
								...floatingStyles,
								width: containerWidth,
								minWidth: containerWidth,
							}}
							className={classNames(
								styles.options,
								tableSelect && styles.tableSelectOptions,
								compactOptions && styles.compactOptions,
							)}
							id="select-options"
							role="listbox"
						>
							{searchable && (
								<div className={styles.search}>
									<SearchIcon className={styles.searchIcon} />
									<input
										type="text"
										value={searchQuery}
										autoFocus
										placeholder="Search..."
										onChange={e => setSearchQuery(e.target.value)}
										aria-label="Search options"
									/>
									{!isEmpty(searchQuery) && (
										<CloseIcon
											className={styles.closeIcon}
											onClick={e => {
												e.stopPropagation();
												setSearchQuery('');
											}}
											aria-label="Clear search"
										/>
									)}
								</div>
							)}
							{isEmpty(displayedOptions) ? (
								<div className={styles.noResults}>No results found</div>
							) : (
								renderOptions()
							)}
						</div>
					</OutsideClickHandler>
				</FloatingPortal>
			)}
			{!!errorMessage && touched && (
				<span className={classNames(styles.errorMessage, tableSelect && styles.tableSelectError)}>
					{errorMessage}
				</span>
			)}
		</FlexBlock>
	);
};

export default Select;
